import { useLayoutEffect, useRef, useState, type ComponentType } from "react";
import { NavLink, Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ChevronLeft,
  LogOut,
  Menu,
  X,
  Building2,
  Users,
  ScrollText,
  Receipt,
  CreditCard,
  Activity,
  PackageOpen,
  Coins,
  Gauge,
  Brain,
  Settings,
  ExternalLink,
} from "lucide-react";

const APP_URL = "https://app.formular.so";
import { useAuth, type EffectiveRole } from "../hooks/useAuth";
import { useOrgSnapshot } from "../hooks/useOrgSnapshot";
import { cn } from "../lib/utils";
import { OwnerAlerts } from "./owner/OwnerAlerts";
import { Auth } from "../lib/apiTyped";
import { useResource } from "../lib/useResource";

// A single navigation item.
export type NavItem = {
  to: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  end?: boolean;
  // Stable selector used by the guided tour to spotlight this item.
  tourId?: string;
};

// Items grouped under a category header. Empty `header` collapses into a
// single bare list (used for short, role-specific menus).
export type NavSection = {
  header?: string;
  items: NavItem[];
};

// The org-brain curation entry. Shown to org owners (via OWNER_NAV) and, at
// runtime, to org admins whose account_type is enterprise_member (injected
// below once we know their org role). Plain members never see it; the API
// (requireOrgAdmin) is the real gate.
const BRAIN_NAV_ITEM: NavItem = {
  to: "/brain",
  label: "Conocimiento",
  icon: Brain,
  tourId: "nav-brain",
};

// Sidebar nav definition per role. The category headings ("PAYMENTS", "API
// ACCESS", "SKILLS", "SETTINGS") match the way the user thinks about the
// three businesses in the app.
const ADMIN_NAV: NavSection[] = [
  {
    items: [
      { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/admin/organizations", label: "Organizations", icon: Building2 },
      { to: "/admin/users", label: "Users", icon: Users },
    ],
  },
  {
    header: "Payments",
    items: [
      { to: "/admin/payments/proposals", label: "Proposals", icon: ScrollText },
      { to: "/admin/payments/invoices", label: "Invoices", icon: Receipt },
    ],
  },
  {
    header: "Subscriptions",
    items: [
      { to: "/admin/api/subscriptions", label: "Subscriptions", icon: CreditCard },
      { to: "/admin/api/plans", label: "Plans", icon: PackageOpen },
      { to: "/admin/api/model-pricing", label: "Model pricing", icon: Coins },
    ],
  },
  {
    header: "Observability",
    items: [
      { to: "/admin/observability", label: "Agentes", icon: Gauge },
    ],
  },
  {
    header: "Audit",
    items: [{ to: "/admin/audit", label: "Audit logs", icon: ScrollText }],
  },
];

const OWNER_NAV: NavSection[] = [
  {
    items: [
      {
        to: "/",
        label: "Overview",
        icon: LayoutDashboard,
        end: true,
        tourId: "nav-overview",
      },
      BRAIN_NAV_ITEM,
    ],
  },
  {
    header: "Subscription",
    items: [
      { to: "/api/team", label: "Team", icon: Users, tourId: "nav-team" },
      {
        to: "/api/usage",
        label: "Usage",
        icon: Activity,
        tourId: "nav-usage",
      },
      {
        to: "/billing",
        label: "Billing",
        icon: Receipt,
        tourId: "nav-billing",
      },
    ],
  },
];

const MEMBER_NAV: NavSection[] = [
  {
    items: [
      {
        to: "/",
        label: "Overview",
        icon: LayoutDashboard,
        end: true,
        tourId: "nav-overview",
      },
    ],
  },
  {
    header: "Subscription",
    items: [
      {
        to: "/api/usage",
        label: "Usage",
        icon: Activity,
        tourId: "nav-usage",
      },
    ],
  },
];

export function navFor(role: EffectiveRole): NavSection[] {
  switch (role) {
    case "admin":
      return ADMIN_NAV;
    case "enterprise_owner":
    case "institution":
      return OWNER_NAV;
    case "enterprise_member":
      return MEMBER_NAV;
    default:
      return [];
  }
}

// Returns a copy of `sections` with the brain-curation entry appended to the
// first group. Never mutates the shared NAV constants.
function withBrainItem(sections: NavSection[]): NavSection[] {
  if (sections.length === 0) return [{ items: [BRAIN_NAV_ITEM] }];
  const [first, ...rest] = sections;
  return [{ ...first, items: [...first.items, BRAIN_NAV_ITEM] }, ...rest];
}

export function AppLayout() {
  const {
    user,
    profile,
    signOut,
    effectiveRole,
    enterpriseId,
    impersonation,
    isRealAdmin,
    startImpersonation,
    stopImpersonation,
  } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // The account_type claim can't tell an org admin apart from a plain member
  // (both are enterprise_member); their ORG role lives in the membership doc.
  // We fetch memberships when they matter: for the platform-admin "switch
  // view" switcher, and to decide whether an enterprise_member is an org admin
  // (who may curate the brain). Owners/institutions never need the fetch.
  const needsMemberships =
    isRealAdmin || effectiveRole === "enterprise_member";
  const memberships = useResource(
    () => (needsMemberships ? Auth.myMemberships() : Promise.resolve(null)),
    [needsMemberships],
  );
  const allMemberships = memberships.data?.memberships ?? [];
  const ownedOrgs = allMemberships.filter((m) => m.role === "owner");
  // Org admins curate the brain too; owners/institutions already have the
  // entry via OWNER_NAV. Plain members never see it (the API is the real gate).
  const isOrgAdmin =
    effectiveRole === "enterprise_member" &&
    allMemberships.some((m) => m.orgId === enterpriseId && m.role === "admin");

  const sections = isOrgAdmin
    ? withBrainItem(navFor(effectiveRole))
    : navFor(effectiveRole);
  const flatItems = sections.flatMap((s) => s.items);
  // Logo de la org: lo cargamos siempre que haya enterpriseId; el snapshot
  // ya respeta la impersonación (un admin viendo como owner ve el logo de
  // esa org). Sin org → no hay logo y el header cae al texto por defecto.
  const { org } = useOrgSnapshot();
  const orgBranding = (org ?? null) as
    | { name?: string; logoUrl?: string | null }
    | null;
  // Account settings is open to every signed-in user, not just owners.
  const showSettings = !!effectiveRole;
  const currentItem = flatItems.find((n) =>
    n.end
      ? location.pathname === n.to
      : n.to !== "/" && location.pathname.startsWith(n.to),
  );

  // Platform admins who also own one or more orgs get a quick switcher to
  // enter that org's owner dashboard via impersonation (ownedOrgs, computed
  // above). Only ownerships are surfaced — impersonating as admin/member
  // would confuse the existing claims path.

  // El banner superior (impersonación o switcher de admin) es sticky y empuja
  // al sidebar — sin compensar su alto, el sidebar `h-screen` excede el
  // viewport y la sección inferior (sign-out + settings) queda cortada.
  // Medimos el alto real con ResizeObserver porque el switcher puede
  // wrappear en varias líneas.
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const [bannerH, setBannerH] = useState(0);
  const hasBanner = !!impersonation || (isRealAdmin && ownedOrgs.length > 0);
  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!el) {
      setBannerH(0);
      return;
    }
    const ro = new ResizeObserver((entries) => {
      setBannerH(entries[0]?.contentRect.height ?? 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasBanner]);

  return (
    <div className="min-h-screen bg-bg">
      {impersonation && (
        <div
          ref={bannerRef}
          className="sticky top-0 z-50 px-4 py-2 flex items-center justify-center gap-3 text-sm"
          style={{
            backgroundColor: "rgba(200,169,81,0.15)",
            borderBottom: "1px solid rgba(200,169,81,0.3)",
          }}
        >
          <span className="text-ink">
            Viendo como{" "}
            <strong className="font-semibold">
              {impersonation.kind === "enterprise_member" ? "member" : "owner"}
            </strong>{" "}
            de{" "}
            <strong className="font-semibold">{impersonation.name}</strong>
          </span>
          <button
            onClick={stopImpersonation}
            className="inline-flex items-center gap-1 text-xs font-medium text-ink hover:opacity-80 bg-surface border border-border rounded-full px-3 py-1 cursor-pointer"
          >
            <X className="w-3.5 h-3.5" />
            Volver a admin
          </button>
        </div>
      )}
      {!impersonation && isRealAdmin && ownedOrgs.length > 0 && (
        <div
          ref={bannerRef}
          className="sticky top-0 z-50 px-4 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm"
          style={{
            backgroundColor: "rgba(0,0,0,0.04)",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <span className="text-ink-2">Cambiar vista:</span>
          {ownedOrgs.map((o) => {
            const isInstitution = o.orgType === "institution";
            return (
              <div key={o.orgId} className="inline-flex items-center gap-1.5">
                <button
                  onClick={() =>
                    startImpersonation({
                      kind: isInstitution ? "institution" : "enterprise",
                      id: o.orgId,
                      name: o.orgName,
                    })
                  }
                  className="inline-flex items-center gap-1 text-xs font-semibold text-ink hover:opacity-80 bg-surface border border-border rounded-full px-3 py-1 cursor-pointer"
                >
                  Owner
                </button>
                {!isInstitution && (
                  <button
                    onClick={() =>
                      startImpersonation({
                        kind: "enterprise_member",
                        id: o.orgId,
                        name: o.orgName,
                      })
                    }
                    className="inline-flex items-center gap-1 text-xs font-semibold text-ink hover:opacity-80 bg-surface border border-border rounded-full px-3 py-1 cursor-pointer"
                  >
                    Member
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="flex">
        <aside
          className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-surface sticky"
          style={{
            height: `calc(100vh - ${bannerH}px)`,
            top: bannerH,
          }}
        >
          <SidebarContent
            sections={sections}
            onSignOut={signOut}
            displayName={profile?.display_name ?? user?.email ?? ""}
            showSettings={showSettings}
            orgName={orgBranding?.name ?? null}
            orgLogoUrl={orgBranding?.logoUrl ?? null}
          />
        </aside>

        {mobileOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          >
            <aside
              className="w-72 bg-surface h-full shadow-glass flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <SidebarContent
                sections={sections}
                onSignOut={signOut}
                displayName={profile?.display_name ?? user?.email ?? ""}
                onClose={() => setMobileOpen(false)}
                showSettings={showSettings}
                orgName={orgBranding?.name ?? null}
                orgLogoUrl={orgBranding?.logoUrl ?? null}
              />
            </aside>
          </div>
        )}

        <main className="flex-1 min-w-0">
          <div className="flex items-center gap-2 lg:hidden border-b border-border px-4 py-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="p-1 text-ink-2 hover:text-ink"
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-ink">
              {currentItem?.label ?? "Formular"}
            </span>
          </div>
          <div className="p-6 lg:p-10">
            <OwnerAlerts />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({
  sections,
  onSignOut,
  displayName,
  onClose,
  showSettings,
  orgName,
  orgLogoUrl,
}: {
  sections: NavSection[];
  onSignOut: () => void;
  displayName: string;
  onClose?: () => void;
  showSettings?: boolean;
  orgName?: string | null;
  orgLogoUrl?: string | null;
}) {
  const navigate = useNavigate();
  // Cuando la org tiene logo, el header se convierte en una banda con el
  // logo ocupando todo el ancho del sidebar (aspect ratio 3:1, típico de
  // marcas con wordmark). object-contain garantiza que un logo cuadrado o
  // muy ancho se centra dentro de la banda sin deformarse. Bajo la banda
  // queda el displayName del usuario; el nombre de la org se mantiene
  // como tooltip (lo aporta el propio logo).
  return (
    <>
      <div className="px-5 pt-5 pb-4 border-b border-border">
        {orgLogoUrl ? (
          <>
            <div
              className="w-full aspect-[3/1] rounded-xl bg-surface-2 border border-border overflow-hidden"
              title={orgName ?? "Formular"}
            >
              <img
                src={orgLogoUrl}
                alt={orgName ?? "Logo"}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
            <p className="text-xs text-ink-3 mt-2 truncate">{displayName}</p>
          </>
        ) : (
          <>
            <a
              href="https://formular.so"
              target="_blank"
              rel="noreferrer noopener"
              className="block text-lg font-semibold text-ink hover:opacity-70 transition-opacity"
            >
              Formular
            </a>
            <p className="text-xs text-ink-3 mt-1 truncate">{displayName}</p>
          </>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
        {sections.map((section, i) => (
          <div key={i}>
            {section.header && (
              <div className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-3">
                {section.header}
              </div>
            )}
            <div className="space-y-0.5">
              {section.items.map(({ to, label, icon: Icon, end, tourId }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={onClose}
                  data-tour-id={tourId}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "bg-ink text-white"
                        : "text-ink-2 hover:bg-surface-2 hover:text-ink",
                    )
                  }
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-border">
        <div className="px-3 pt-3">
          <a
            href={APP_URL}
            target="_blank"
            rel="noreferrer noopener"
            onClick={onClose}
            data-tour-id="open-app-button"
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-ink text-white hover:opacity-90 transition-opacity cursor-pointer"
          >
            <ExternalLink className="w-4 h-4" />
            Abrir app
          </a>
        </div>
        <div className="px-3 py-3 flex items-center gap-2">
          <button
            onClick={onSignOut}
            className="flex-1 flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-ink-2 hover:bg-surface-2 hover:text-ink transition-colors cursor-pointer"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
          {showSettings && (
            <button
              onClick={() => {
                onClose?.();
                navigate("/settings");
              }}
              data-tour-id="settings-button"
              className="p-2 rounded-lg text-ink-2 hover:bg-surface-2 hover:text-ink transition-colors cursor-pointer"
              aria-label="Ajustes"
              title="Ajustes"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
        </div>
        {orgLogoUrl && (
          // Co-branding: solo mostramos "Powered by" cuando el header está
          // ocupado por el logo del cliente. Sin logo, el wordmark ya vive
          // arriba como cabecera principal, así que duplicarlo aquí sería
          // redundante.
          <div className="px-5 pb-4 -mt-1">
            <a
              href="https://formular.so"
              target="_blank"
              rel="noreferrer noopener"
              className="text-[10px] text-ink-3 hover:text-ink-2 transition-colors"
            >
              Powered by Formular
            </a>
          </div>
        )}
      </div>
    </>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
  backTo,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  backTo?: string;
}) {
  return (
    <div className="mb-8">
      {backTo && (
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink transition-colors mb-3"
        >
          <ChevronLeft className="w-3 h-3" />
          Back
        </Link>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-black text-ink leading-tight tracking-tight">
            {title}
          </h1>
          {subtitle && <p className="text-sm text-ink-3 mt-1">{subtitle}</p>}
        </div>
        {action}
      </div>
    </div>
  );
}
